/**
 * Conteúdo do Modo Sofá: tipos de inimigo, itens e dificuldade.
 *
 * Só tabelas — sem lógica. Ajustar balanceamento é mexer aqui; o simulador
 * (sofaSim.ts) lê estes números e os testes de balanceamento os exercitam.
 */

export type SofaDifficulty = "easy" | "normal" | "hard";

export interface DifficultyTuning {
  botHealth: number;
  botDamage: number;
  /** Multiplica o intervalo entre tiros (maior = atira menos). */
  fireInterval: number;
  botCount: number;
  aimError: number;
}

export const DIFFICULTY: Record<SofaDifficulty, DifficultyTuning> = {
  easy: { botHealth: 0.75, botDamage: 0.7, fireInterval: 1.3, botCount: 0.8, aimError: 1.5 },
  normal: { botHealth: 1, botDamage: 1, fireInterval: 1, botCount: 1, aimError: 1 },
  hard: { botHealth: 1.3, botDamage: 1.3, fireInterval: 0.8, botCount: 1.25, aimError: 0.7 },
};

export const SOFA_DIFFICULTIES: SofaDifficulty[] = ["easy", "normal", "hard"];

// ─── Inimigos ────────────────────────────────────────────────────────────────

/**
 * Mesmos papéis e ids do modo solo (waveGenerator: normal, fast, heavy,
 * sniper, boss) — e os mesmos nomes na tela (infinite.enemyKinds).
 */
export type BotKind = "normal" | "fast" | "heavy" | "sniper" | "boss";

export interface BotKindStats {
  radius: number;
  speed: number;
  health: number;
  damage: number;
  bulletSpeed: number;
  bulletSize: number;
  fireMin: number;
  fireMax: number;
  range: number;
  /** Distância que tenta manter do alvo. */
  preferred: number;
  /** Pontos pelo abate (placar da Disputa e do time). */
  score: number;
  /** Tiros seguidos por disparo (rajada). */
  burst: number;
  /** Mira travada antes do tiro (laser visível), em segundos. 0 = sem aviso. */
  telegraph: number;
  /** Dano em obstáculo destrutível por tiro. */
  obstacleDamage: number;
}

export const BOT_KINDS: Record<BotKind, BotKindStats> = {
  normal: {
    radius: 1.1,
    speed: 6.5,
    health: 40,
    damage: 10,
    bulletSpeed: 20,
    bulletSize: 0.25,
    fireMin: 1.2,
    fireMax: 2.0,
    range: 22,
    preferred: 9,
    score: 100,
    burst: 1,
    telegraph: 0,
    obstacleDamage: 1,
  },
  fast: {
    radius: 0.9,
    speed: 10,
    health: 22,
    damage: 6,
    bulletSpeed: 24,
    bulletSize: 0.2,
    fireMin: 1.0,
    fireMax: 1.5,
    range: 14,
    preferred: 5,
    score: 80,
    burst: 2,
    telegraph: 0,
    obstacleDamage: 1,
  },
  heavy: {
    radius: 1.45,
    speed: 4,
    health: 110,
    damage: 22,
    bulletSpeed: 15,
    bulletSize: 0.5,
    fireMin: 2.2,
    fireMax: 3.0,
    range: 20,
    preferred: 11,
    score: 200,
    burst: 1,
    telegraph: 0,
    obstacleDamage: 2,
  },
  sniper: {
    radius: 1.1,
    speed: 5,
    health: 35,
    damage: 26,
    bulletSpeed: 42,
    bulletSize: 0.22,
    fireMin: 3.0,
    fireMax: 4.0,
    range: 34,
    preferred: 18,
    score: 150,
    burst: 1,
    telegraph: 0.9,
    obstacleDamage: 1,
  },
  boss: {
    radius: 2.2,
    speed: 3.2,
    health: 700,
    damage: 14,
    bulletSpeed: 17,
    bulletSize: 0.4,
    fireMin: 1.6,
    fireMax: 2.2,
    range: 30,
    preferred: 12,
    score: 1500,
    burst: 1,
    telegraph: 0,
    obstacleDamage: 2,
  },
};

/** Escala visual de cada tipo — a mesma `size` do modo solo. */
export const BOT_SIZE: Record<BotKind, number> = {
  normal: 1,
  fast: 0.8,
  heavy: 1.35,
  sniper: 1,
  boss: 2,
};

/** Canos visíveis — o `shotCount` que o solo dá a cada tipo. */
export const BOT_BARRELS: Record<BotKind, number> = {
  normal: 1,
  fast: 1,
  heavy: 2,
  sniper: 1,
  boss: 3,
};

/** Cada 5ª onda tem chefe. */
export const BOSS_EVERY = 5;

/** Onda a partir da qual cada tipo aparece. */
export const BOT_UNLOCK_WAVE: Record<Exclude<BotKind, "boss">, number> = {
  normal: 1,
  fast: 2,
  heavy: 3,
  sniper: 4,
};

// ─── Itens ───────────────────────────────────────────────────────────────────

/**
 * Os itens do sofá SÃO itens do jogo: mesmos ids, nomes, ícones e cores do
 * `ITEM_CONFIG` (useRocketanks), e os mesmos números da sala multiplayer
 * (server/colyseus/systems/ItemSystem.ts). Como nos outros modos, a Cura vale
 * na hora; os demais ficam guardados (um por vez) e são usados com LB/X ou
 * E/Q, e só um efeito fica ativo por vez.
 */
export type SofaItemKind = "heal" | "shield" | "turbo" | "intense" | "minigun" | "freeze";

export const SOFA_ITEM_KINDS: SofaItemKind[] = [
  "heal",
  "shield",
  "turbo",
  "intense",
  "minigun",
  "freeze",
];

export const ITEM_WEIGHTS: Record<SofaItemKind, number> = {
  heal: 25,
  shield: 18,
  turbo: 17,
  intense: 15,
  minigun: 15,
  freeze: 10,
};

/** Duração de cada efeito em segundos (0 = instantâneo). Espelha POWERUP_DURATION_MS. */
export const ITEM_DURATION: Record<SofaItemKind, number> = {
  heal: 0,
  shield: 6,
  turbo: 4,
  intense: 5,
  minigun: 5,
  freeze: 5,
};

export const ITEM = {
  HEAL_AMOUNT: 30,
  SHIELD_HITS: 4,
  TURBO_MULT: 1.6,
  INTENSE_DAMAGE_MULT: 1.5,
  /** Metralhadora: tiros por disparo (em leque) e dano total da rajada. */
  MINIGUN_SHOTS: 2,
  MINIGUN_DAMAGE_MULT: 0.7,
  /** Quanto tempo o tiro congelante paralisa o alvo. */
  FREEZE_HIT_TIME: 1,
  FIRST_SPAWN: 10,
  SPAWN_MIN: 8,
  SPAWN_MAX: 12,
  MAX_ON_FIELD: 3,
  LIFETIME: 18,
  // Mesmo raio de coleta do item do jogo (Item.tsx)
  PICKUP_RADIUS: 2.5,
} as const;
